import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../context/ToastContext';
import { fetchLibraryDashboard, fetchOverdueList, returnBook } from '../../slices/librarySlice';
import {
  C, FONT, Panel, StatTile, TileGrid, BookCover, Pill, EmptyState, formatDate
} from './libraryUi';
import AutoStoriesIcon from '@mui/icons-material/AutoStoriesOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AlarmIcon from '@mui/icons-material/AlarmOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEventsOutlined';

const LibraryDashboardTab = ({ onOpenTab }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { dashboard, loading, error, saving } = useSelector((state) => state.library);
  const [overdue, setOverdue] = useState([]);

  const load = useCallback(() => {
    dispatch(fetchLibraryDashboard());
    fetchOverdueList().then(setOverdue).catch(() => setOverdue([]));
  }, [dispatch]);

  // Refetch every time the tab opens so borrow/return changes show up
  useEffect(() => {
    load();
  }, [load]);

  const handleReturn = async (id) => {
    try {
      const result = await dispatch(returnBook(id)).unwrap();
      const fine = Number(result.fine_amount || 0);
      showToast(fine > 0 ? `تم الإرجاع - غرامة تأخير: ${fine}` : 'تم الإرجاع بنجاح', fine > 0 ? 'warning' : 'success');
      load();
    } catch (err) {
      showToast(err?.error || 'حدث خطأ', 'error');
    }
  };

  if (loading && !dashboard) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress sx={{ color: C.primary }} />
      </Box>
    );
  }

  if (error && !dashboard) {
    return <Alert severity="error" sx={{ borderRadius: 3 }}>{error.error || 'حدث خطأ في تحميل البيانات'}</Alert>;
  }

  if (!dashboard) return null;

  if (!dashboard.total_items) {
    return (
      <EmptyState
        icon={<AutoStoriesIcon />}
        title="المكتبة فارغة"
        hint="ابدأ بإضافة الكتب، ثم يمكنك إعارتها للطلاب والموظفين"
        action={
          <Button variant="contained" onClick={() => onOpenTab?.(1)} sx={{ borderRadius: 2.5, fontFamily: FONT, fontWeight: 700, bgcolor: C.primary, boxShadow: 'none' }}>
            إضافة كتاب
          </Button>
        }
      />
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <TileGrid min={220}>
        <StatTile
          label="الكتب"
          value={dashboard.total_items}
          hint={`${dashboard.total_copies} نسخة`}
          icon={<AutoStoriesIcon />}
        />
        <StatTile
          label="متاح للاستعارة"
          value={dashboard.available_copies}
          icon={<CheckCircleIcon />}
          tone="success"
        />
        <StatTile
          label="مستعار الآن"
          value={dashboard.active_borrows}
          hint={`اليوم: ${dashboard.checkouts_today} استعارة · ${dashboard.returns_today} إرجاع`}
          icon={<SwapHorizIcon />}
          tone="warning"
        />
        <StatTile
          label="متأخر"
          value={dashboard.overdue_count}
          hint={dashboard.total_fines > 0 ? `الغرامات: ${dashboard.total_fines}` : undefined}
          icon={<AlarmIcon />}
          tone={dashboard.overdue_count > 0 ? 'danger' : 'neutral'}
        />
      </TileGrid>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        <Panel
          title="تحتاج متابعة"
          subtitle="كتب تجاوزت موعد الإرجاع"
          action={
            <Button size="small" onClick={() => onOpenTab?.(2)} sx={{ fontFamily: FONT, fontWeight: 700, color: C.primary }}>
              كل الاستعارات
            </Button>
          }
        >
          {overdue.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ color: C.success, fontSize: 36 }} />
              <Typography color={C.muted} sx={{ fontFamily: FONT, mt: 0.5 }}>
                لا توجد كتب متأخرة 👌
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={1}>
              {overdue.slice(0, 6).map((t) => (
                <Box
                  key={t.id}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25,
                    borderRadius: 3, bgcolor: C.dangerSoft,
                  }}
                >
                  <BookCover title={t.copy_title} width={34} />
                  <Box flex={1} minWidth={0}>
                    <Typography fontWeight={700} color={C.text} noWrap sx={{ fontFamily: FONT }}>
                      {t.copy_title}
                    </Typography>
                    <Typography variant="body2" color={C.muted} noWrap sx={{ fontFamily: FONT }}>
                      {t.member_name} · كان موعده {formatDate(t.due_date)}
                    </Typography>
                  </Box>
                  <Pill label={`${t.overdue_days} يوم`} tone="danger" />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={saving}
                    onClick={() => handleReturn(t.id)}
                    sx={{ borderRadius: 2, fontFamily: FONT, fontWeight: 700, borderColor: C.border, color: C.text, bgcolor: '#fff' }}
                  >
                    إرجاع
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Panel>

        <Panel title="الأكثر استعارة" action={<EmojiEventsIcon sx={{ color: C.warning }} />}>
          {dashboard.most_borrowed?.length ? (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {dashboard.most_borrowed.map((item, index) => (
                <Box key={item.id} display="flex" alignItems="center" gap={1.5}>
                  <Typography fontWeight={800} color={index === 0 ? C.warning : C.muted} sx={{ fontFamily: FONT, width: 18 }}>
                    {index + 1}
                  </Typography>
                  <BookCover title={item.title} width={32} />
                  <Box flex={1} minWidth={0}>
                    <Typography fontWeight={700} color={C.text} noWrap sx={{ fontFamily: FONT }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color={C.muted} noWrap sx={{ fontFamily: FONT }}>
                      {item.author || 'غير محدد'}
                    </Typography>
                  </Box>
                  <Pill label={`${item.borrow_count} ×`} tone="primary" />
                </Box>
              ))}
            </Box>
          ) : (
            <Typography color={C.muted} textAlign="center" py={3} sx={{ fontFamily: FONT }}>
              لم تتم أي استعارة بعد
            </Typography>
          )}
        </Panel>
      </Box>
    </Box>
  );
};

export default LibraryDashboardTab;
